<template>
  <div class="gf_control-inumber">
    <div class="gf_control-inumber__container paragraph-12-medium" :style="styleContainer">
      <div v-if="isDisplayNumberButton" class="gf_control-inumber__button" @mouseleave="controlMouseLeave">
        <button
          type="button"
          class="gf_control-inumber__button-action"
          :disabled="readonly"
          @click="controlClick(-1)"
          @mousedown="doChange(-1)"
          @mouseup="controlRelease"
          @mouseleave="controlMouseLeave">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="3.5" y1="7.5" x2="12.5" y2="7.5" stroke="#E2E2E2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
      <div class="gf_control-inumber__counter">
        <input
          class="gf_control-inumber__counter--input"
          type="number"
          :value="val"
          :placeholder="placeholder"
          :disabled="readonly"
          :class="mergedInputClass"
          :min="min"
          :max="max"
          lang="en"
          @keyup.prevent="onChange"
          @input="onChange"
          @change.prevent="onChange"
          @blur.prevent="blur"
          @focus="$event.target.select()" />
      </div>
      <div v-if="isDisplayNumberButton" class="gf_control-inumber__button">
        <button
          type="button"
          class="gf_control-inumber__button-action"
          :disabled="readonly"
          @click="controlClick(1)"
          @mousedown="doChange(1)"
          @mouseup="controlRelease"
          @mouseleave="controlMouseLeave">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M8.5 3C8.5 2.72386 8.27614 2.5 8 2.5C7.72386 2.5 7.5 2.72386 7.5 3V7.5H3C2.72386 7.5 2.5 7.72386 2.5 8C2.5 8.27614 2.72386 8.5 3 8.5H7.5V13C7.5 13.2761 7.72386 13.5 8 13.5C8.27614 13.5 8.5 13.2761 8.5 13V8.5H13C13.2761 8.5 13.5 8.27614 13.5 8C13.5 7.72386 13.2761 7.5 13 7.5H8.5V3Z"
              fill="#E2E2E2" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InputNumber',
  props: {
    id: [String, Number],
    value: {
      type: [String, Number],
      default: 0,
    },
    styleContainer: {
      type: Object,
      default: () => {},
    },
    placeholder: String,
    readonly: {
      type: Boolean,
      required: false,
      default() {
        return true;
      },
    },
    min: {
      type: Number,
      default: -999999,
    },
    max: {
      type: Number,
      default: 999999,
    },
    decimal: {
      type: Number,
      default: 0,
    },
    isDisplayNumberButton: {
      type: Boolean,
      default: true,
    },
    inputClass: {
      type: String,
      default: '',
    },
  },
  emits: ['onChange', 'created', 'blur'],
  data() {
    return {
      val: 0,
      interval: null,
      timeout: null,
      pressing: false,
      internalClass: '',
      oldVal: 0,
    };
  },
  computed: {
    mergedInputClass() {
      return [this.inputClass, this.internalClass].join(' ');
    },
  },
  watch: {
    value(newV, oldV) {
      if (newV === oldV) return;
      if (typeof this.value === 'number') this.val = newV;
      else this.val = parseFloat(newV);
      this.val = this.checkRangeValue(this.val);
      this.oldVal = this.val;
    },
  },
  created() {
    this.$emit('created', this);
    if (typeof this.value === 'number') this.val = this.value;
    else this.val = parseFloat(this.value);
    this.oldVal = this.val;
  },
  methods: {
    doChange(changing) {
      this.timeout = setTimeout(() => {
        this.interval = setInterval(() => {
          this.pressing = true;
          this.controlPress(changing);
        }, 100);
      }, 500);
    },
    controlPress(change) {
      let val = this.val;
      val = this.val !== '' ? parseFloat(this.val) : 0;
      val += change;
      this.val = this.checkRangeValue(val);
      this.onEmitValue();
    },
    controlRelease() {
      clearInterval(this.interval);
      clearTimeout(this.timeout);
      this.interval = null;
      this.timeout = null;
      if (typeof this.interval === 'number') {
        this.onEmitValue();
      }
    },
    controlMouseLeave() {
      if (typeof this.interval === 'number') {
        clearInterval(this.interval);
        clearTimeout(this.timeout);
        this.interval = null;
        this.timeout = null;
        this.onEmitValue();
      }
    },
    checkRangeValue(value) {
      if (value > this.max || value < this.min) return this.oldVal;
      return value;
    },
    controlClick(change) {
      let val = this.val !== '' ? parseFloat(this.val) : 0;
      val += change;
      this.val = this.checkRangeValue(val);
      this.onEmitValue();
    },
    setValue(value) {
      if (value != this.val && value) {
        if (value !== '') this.val = parseFloat(value);
        else this.val = 0;
        this.val = this.checkRangeValue(this.val);
      }
    },
    parseValue() {
      if (this.val !== '') this.val = parseFloat(this.val);
    },
    onEmitValue() {
      this.$emit('onChange', { id: this.id, val: this.val });
    },
    onChange(event) {
      let value = event?.target?.value;
      if (value === '') {
        return;
      }
      if (this.decimal) {
        value = parseFloat(value);
        this.onEmitValue();
      } else {
        if (this.val != value || event.key == '.') {
          const newV = parseInt(value);
          if (Number.isInteger(newV) && newV >= this.min && newV <= this.max) {
            this.val = newV;
            this.internalClass = '';
            if (event) {
              event.target.value = this.val;
              event.target.type = 'text';
              event.target.setSelectionRange(this.max, this.max);
              event.target.type = 'number';
            }
            this.onEmitValue();
          } else {
            this.internalClass = 'error';
            setTimeout(() => {
              if (event) {
                event.target.value = value;
                event.target.type = 'text';
                event.target.setSelectionRange(this.max, this.max);
                event.target.type = 'number';
              }
            });
          }
        }
      }
    },
    blur(event) {
      this.val = event.target.value;
      this.resetValue();
      const val = this.checkRangeValue(this.val);
      this.$emit('blur', { id: this.id, val });
      this.internalClass = '';
    },
    resetValue() {
      if (!Number.isInteger(this.val) || this.val < this.min || this.val > this.max) {
        if (this.val === '') {
          this.val = 0 < this.min ? this.min : 0;
        } else {
          this.val = this.oldVal;
        }
        this.internalClass = '';
        this.onEmitValue();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.gf_control-inumber {
  &__container {
    width: auto;
    display: flex;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    border: 1px solid map-get($hybrid-shade-dark, '200');
    background-color: map-get($hybrid-shade-dark, '300');
    border-radius: 3px;
    color: map-get($text-dark-mode, 'high-contrast');
  }
  &__button {
    width: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    &-action {
      border: 0;
      border-radius: 0;
      background: map-get($hybrid-shade-dark, '300');
      padding: 8px;
    }
  }
  &__counter {
    min-width: 34px;
    &--input {
      min-width: inherit;
      border: none;
      box-sizing: border-box;
      transition: box-shadow 0.3s;
      background: map-get($hybrid-shade-dark, '300');
      color: map-get($text-dark-mode, 'high-contrast');
      text-align: center;
      padding: 8px;
      &:focus {
        border: solid 1px #4f46ba;
      }
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
      }
      -moz-appearance: textfield;
      &.error {
        border: 1px solid $accent-red-new !important;
      }
    }
  }
}
</style>
